import React, { useCallback, useRef, useState } from "react"
import TextField, { TextFieldProps } from "@material-ui/core/TextField"
import AutoComplete, {
  AutocompleteClassKey,
} from "@material-ui/lab/Autocomplete"
import CircularProgress from "@material-ui/core/CircularProgress"
import DropdownIcon from "@material-ui/icons/KeyboardArrowDown"
import { cx } from "@emotion/css"
import { styles } from "./styles"
import { useI18n } from "providers/I18n"
import { FilterOptionsState } from "@material-ui/lab"

export type AutoCompleteInputProps<T> = {
  isLoading?: boolean
  options?: Array<T>
  onInputChange?: (value: string) => void
  onChange?: (value: T | null) => void
  label: string
  disableClearable?: boolean
  getOptionLabel: (val: T) => string
  blurOnChange?: boolean
  getOptionSelected: (option: T, val: T) => boolean
  renderOption: (option: T) => React.ReactNode
  filterOptions?:
    | ((options: T[], state: FilterOptionsState<T>) => T[])
    | undefined
  value?: T
  errorMessage?: string
  disabled?: boolean
  classes: Partial<Record<AutocompleteClassKey, string>>
} & Omit<TextFieldProps, "onChange">

function AutoCompleteInput<T>(props: AutoCompleteInputProps<T>) {
  const [search, setSearch] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)
  const {
    isLoading,
    options = [],
    onInputChange,
    onChange,
    error,
    helperText,
    errorMessage,
    disableClearable,
    blurOnChange = false,
    getOptionLabel,
    getOptionSelected,
    renderOption,
    size,
    filterOptions,
    value,
    margin = "none",
    disabled = false,
    classes,
    ...rest
  } = props

  const { t } = useI18n()
  const _error = !!errorMessage || error
  const helper = _error ? t(errorMessage!) : helperText

  const handleInputChange = useCallback(
    (_: React.ChangeEvent<{}>, value: string) => {
      onInputChange?.(value)
      setSearch(value)
    },
    [onInputChange],
  )

  const handleChange = useCallback(
    (_: React.ChangeEvent<{}>, value: T | null) => {
      !!onChange && onChange(value)
      if (blurOnChange) inputRef?.current?.blur()
    },
    [blurOnChange, onChange],
  )

  const handleFocus = () => setSearch("")

  return (
    <AutoComplete
      fullWidth
      value={value}
      size={size}
      inputValue={search}
      options={options}
      loading={isLoading}
      disabled={disabled}
      onFocus={handleFocus}
      onInputChange={handleInputChange}
      onChange={handleChange}
      disableClearable={disableClearable}
      getOptionSelected={getOptionSelected}
      getOptionLabel={getOptionLabel}
      popupIcon={<DropdownIcon className="icon" />}
      filterOptions={filterOptions}
      renderOption={renderOption}
      disablePortal={true}
      selectOnFocus
      openOnFocus
      renderInput={(params) => (
        <TextField
          {...rest}
          {...params}
          inputRef={inputRef}
          variant="outlined"
          margin={margin}
          error={!!error}
          helperText={helper}
          InputProps={{
            ...params.InputProps,
            autoComplete: "off",
            classes: { root: cx(styles.indicator, styles.disabled) },
            endAdornment: (
              <React.Fragment>
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : (
                  params.InputProps.endAdornment
                )}
              </React.Fragment>
            ),
          }}
        />
      )}
      classes={classes}
    />
  )
}

export default AutoCompleteInput
